/**
 * Product owner's email.  The owner sees an inline "Take down" action
 * on each JobCard; no separate admin panel exists for Peerlist.
 */
export const OWNER_EMAIL = 'dharmendra.sp@gmail.com';

/** Product owner / admin gate (model picker, Ollama diagnostics, etc.) */
export function isOwnerAdminEmail(email: string | null | undefined): boolean {
  return (email ?? '').trim().toLowerCase() === OWNER_EMAIL.toLowerCase();
}

export type FreshnessTranslate = (
  key: string,
  options?: { defaultValue?: string; count?: number },
) => string;

/**
 * Human-readable relative posted time. Returns `''` when `iso` is missing
 * or not a valid date — safe to render behind `{label && ...}`.
 */
export function freshnessLabel(
  iso: string | undefined | null,
  t: FreshnessTranslate,
  locale?: string,
): string {
  if (iso == null || iso === '') return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';

  const now = new Date();
  const ms = now.getTime() - d.getTime();
  if (ms < 0) return d.toLocaleDateString(locale);

  const totalHours = Math.floor(ms / (60 * 60 * 1000));
  if (totalHours < 24) {
    if (totalHours < 1) {
      const minutes = Math.floor(ms / 60_000);
      if (minutes < 1) {
        return t('dashboard.overview.timeJustNow', 'just now');
      }
      return t('dashboard.overview.timeMinutesAgo', { defaultValue: '{{count}}m ago', count: minutes });
    }
    return t('dashboard.overview.timeHoursAgo', { defaultValue: '{{count}}h ago', count: totalHours });
  }

  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  if (days === 1) return t('dashboard.aiCompanion.yesterday', 'Yesterday');
  if (days < 7) return t('dashboard.overview.timeDaysAgo', { defaultValue: '{{count}}d ago', count: days });
  return d.toLocaleDateString(locale);
}

function normalizeMetaToken(value: string): string {
  return value.toLowerCase().replace(/[\s_-]+/g, '');
}

/** Map ingested English job_type strings to localized labels when recognized. */
export function localizeJobType(raw: string | undefined | null, t: FreshnessTranslate): string {
  if (!raw?.trim()) return '';
  const n = normalizeMetaToken(raw);
  const keyByNorm: Record<string, string> = {
    fulltime: 'dashboard.jobs.jobPreferences.jobType.fulltime',
    parttime: 'dashboard.jobs.jobPreferences.jobType.parttime',
    contract: 'dashboard.jobs.jobPreferences.jobType.contract',
    internship: 'dashboard.jobs.jobPreferences.jobType.internship',
    hybrid: 'dashboard.jobs.jobCard.hybrid',
  };
  const key = keyByNorm[n];
  return key ? t(key, { defaultValue: raw }) : raw;
}

/** Map ingested English experience_level strings to localized labels when recognized. */
export function localizeExperienceLevel(raw: string | undefined | null, t: FreshnessTranslate): string {
  if (!raw?.trim()) return '';
  const n = normalizeMetaToken(raw);
  const keyByNorm: Record<string, string> = {
    junior: 'dashboard.jobs.jobPreferences.experience.junior',
    mid: 'dashboard.jobs.jobPreferences.experience.mid',
    midlevel: 'dashboard.jobs.jobPreferences.experience.mid',
    senior: 'dashboard.jobs.jobPreferences.experience.senior',
    lead: 'dashboard.jobs.jobPreferences.experience.lead',
    associate: 'dashboard.jobs.jobCard.experienceAssociate',
    entry: 'dashboard.jobs.jobCard.experienceEntry',
    entrylevel: 'dashboard.jobs.jobCard.experienceEntry',
  };
  const key = keyByNorm[n];
  return key ? t(key, { defaultValue: raw }) : raw;
}

/** @deprecated Misleading name — Peerlist ingests are external channel jobs stored in peerlist_jobs. */
export function isInternalDbJob(job: {
  source?: string;
  source_channel?: string | null;
}): boolean {
  const ch = (job.source_channel ?? '').toLowerCase();
  if (ch === 'linkedin' || ch === 'naukri' || ch === 'indeed') return false;
  return job.source === 'peerlist' || !ch || ch === 'peerlist';
}

/** Jobs ingested via LinkedIn / Naukri / Indeed APIs (also stored in peerlist_jobs). */
export function isExternalDbJob(job: {
  source?: string;
  source_channel?: string | null;
}): boolean {
  const ch = (job.source_channel ?? '').toLowerCase();
  return ch === 'linkedin' || ch === 'naukri' || ch === 'indeed';
}

/**
 * Canonical deduplication key for a job listing.
 * Format: normalized_title|lower_company|first_location_token
 * Safe to use across sources — currently Peerlist only, reserved for future.
 */
export function jobDedupeKey(j: {
  title: string;
  company: string;
  location?: string;
}): string {
  const title = j.title.toLowerCase().replace(/\s+/g, '');
  const company = j.company.toLowerCase().trim();
  const locToken = (j.location ?? '').toLowerCase().split(/[\s,]+/)[0] ?? '';
  return `${title}|${company}|${locToken}`;
}

// ---------------------------------------------------------------------------
// Junk-row detection / sanitization
// ---------------------------------------------------------------------------
// Historical note: an earlier version of parseJobWithAI returned placeholder
// rows when the Groq LLM was rate-limited. Those placeholders were persisted
// and show up today as "Company Name" / "Position Title" / a single skill
// chip reading "Skills extraction unavailable…".  We now reject such rows
// client-side until the next ingest cycle re-parses them.

const JUNK_COMPANIES = new Set(['Company Name', 'Company', 'Unknown company']);
const JUNK_TITLES    = new Set(['Position Title', 'Job Title', 'Unknown job']);
const JUNK_SKILL_RE  = /skills extraction unavailable/i;
const JUNK_REQ_RE    = /rate limit reached/i;

/** Drops skill chips that are pure rate-limit placeholders. */
export function cleanSkills(skills: string[] | undefined | null): string[] {
  if (!Array.isArray(skills)) return [];
  return skills.filter(s => typeof s === 'string' && s.length > 0 && !JUNK_SKILL_RE.test(s));
}

export interface JunkCandidate {
  company?: string;
  title?: string;
  skills?: string[];
  description?: string;
}

// ---------------------------------------------------------------------------
// Markdown → section splitter (used by JobDetailDrawer)
// ---------------------------------------------------------------------------

export interface MarkdownSection {
  /** `h2` / `h3` heading text verbatim, or empty for the preamble block. */
  title: string;
  /** Heading level (2 or 3). 0 for the preamble. */
  level: 0 | 2 | 3;
  /** Remaining markdown body beneath the heading (may be empty). */
  body: string;
  /** Derived bucket — "about_us" for company-intro sections, else "opportunity". */
  bucket: 'opportunity' | 'about_us';
}

/**
 * Classify a section heading. Everything that looks like a company-intro
 * ("About <Company>", "About Us", "About the Company", "Why Join <Company>",
 * "Our Values", "Benefits", "Why You'll Love It Here") goes into the
 * `about_us` bucket. Everything else stays in `opportunity`.
 */
function bucketFor(title: string, companyName: string): 'opportunity' | 'about_us' {
  const t = title.trim().toLowerCase();
  if (!t) return 'opportunity';
  const companyLc = companyName.trim().toLowerCase();
  if (companyLc && (t === `about ${companyLc}` || t === `about us` || t === `about the company`)) {
    return 'about_us';
  }
  if (/^about\s+\S+/.test(t) && !/about\s+(the|this)\s+(role|opportunity|position|job|team|internship)/.test(t)) {
    return 'about_us';
  }
  if (t.startsWith('why join')) return 'about_us';
  if (t === 'our values') return 'about_us';
  if (t === 'benefits') return 'about_us';
  if (t === 'perks') return 'about_us';
  if (t === 'why you\u2019ll love it here' || t === "why you'll love it here") return 'about_us';
  return 'opportunity';
}

/**
 * Split a markdown string by `## ` / `### ` headings into an ordered list of
 * sections, bucketing each as either "opportunity" or "about_us" so the
 * drawer can render them in two visually distinct groups.
 *
 * Designed to be tolerant of legacy plain-text descriptions: when the input
 * contains no headings, a single `{ level: 0, title: '', … }` section is
 * returned with the whole body in the `opportunity` bucket.
 */
export function splitMarkdownSections(
  md: string | undefined | null,
  companyName = '',
): MarkdownSection[] {
  if (!md) return [];
  const lines = md.split('\n');

  const sections: MarkdownSection[] = [];
  let currentTitle = '';
  let currentLevel: 0 | 2 | 3 = 0;
  let buffer: string[] = [];

  const flush = () => {
    const body = buffer.join('\n').trim();
    if (!body && !currentTitle) return;
    sections.push({
      title: currentTitle,
      level: currentLevel,
      body,
      bucket: bucketFor(currentTitle, companyName),
    });
  };

  for (const line of lines) {
    const h2 = line.match(/^##\s+(.+?)\s*$/);
    const h3 = line.match(/^###\s+(.+?)\s*$/);
    if (h2) {
      flush();
      currentTitle = h2[1];
      currentLevel = 2;
      buffer = [];
      continue;
    }
    if (h3) {
      flush();
      currentTitle = h3[1];
      currentLevel = 3;
      buffer = [];
      continue;
    }
    buffer.push(line);
  }
  flush();

  return sections;
}

/** Map common English markdown section headings to localized labels. */
export function localizeSectionTitle(
  title: string,
  t: FreshnessTranslate,
  companyName = '',
): string {
  const raw = title.trim();
  if (!raw) return '';

  const n = raw.toLowerCase().replace(/\s+/g, ' ');
  const companyLc = companyName.trim().toLowerCase();

  const exact: Record<string, { key: string; defaultValue: string }> = {
    'job description': { key: 'dashboard.jobs.sectionTitles.jobDescription', defaultValue: 'Job Description' },
    responsibilities: { key: 'dashboard.jobs.sectionTitles.responsibilities', defaultValue: 'Responsibilities' },
    'key responsibilities': { key: 'dashboard.jobs.sectionTitles.responsibilities', defaultValue: 'Responsibilities' },
    qualifications: { key: 'dashboard.jobs.sectionTitles.qualifications', defaultValue: 'Qualifications' },
    requirements: { key: 'dashboard.jobs.sectionTitles.requirements', defaultValue: 'Requirements' },
    'required skills': { key: 'dashboard.jobs.sectionTitles.requiredSkills', defaultValue: 'Required Skills' },
    'required education': { key: 'dashboard.jobs.sectionTitles.requiredEducation', defaultValue: 'Required Education' },
    'required experience': { key: 'dashboard.jobs.sectionTitles.requiredExperience', defaultValue: 'Required Experience' },
    'about us': { key: 'dashboard.jobs.sectionTitles.aboutUs', defaultValue: 'About Us' },
    'about the company': { key: 'dashboard.jobs.sectionTitles.aboutCompany', defaultValue: 'About the Company' },
    benefits: { key: 'dashboard.jobs.sectionTitles.benefits', defaultValue: 'Benefits' },
    perks: { key: 'dashboard.jobs.sectionTitles.perks', defaultValue: 'Perks' },
    'our values': { key: 'dashboard.jobs.sectionTitles.ourValues', defaultValue: 'Our Values' },
    'why join us': { key: 'dashboard.jobs.sectionTitles.whyJoinUs', defaultValue: 'Why Join Us' },
    "why you'll love it here": { key: 'dashboard.jobs.sectionTitles.whyLoveItHere', defaultValue: "Why You'll Love It Here" },
    'what you will do': { key: 'dashboard.jobs.sectionTitles.whatYouWillDo', defaultValue: 'What You Will Do' },
    'what you\'ll do': { key: 'dashboard.jobs.sectionTitles.whatYouWillDo', defaultValue: 'What You Will Do' },
    'who you are': { key: 'dashboard.jobs.sectionTitles.whoYouAre', defaultValue: 'Who You Are' },
    'nice to have': { key: 'dashboard.jobs.sectionTitles.niceToHave', defaultValue: 'Nice to Have' },
    'good to have': { key: 'dashboard.jobs.sectionTitles.goodToHave', defaultValue: 'Good to Have' },
  };

  const hit = exact[n];
  if (hit) return t(hit.key, { defaultValue: hit.defaultValue });

  if (companyLc && n === `about ${companyLc}`) {
    return t('dashboard.jobs.sectionTitles.aboutCompanyNamed', {
      defaultValue: 'About {{company}}',
      company: companyName,
    });
  }
  if (n.startsWith('about ') && !/^about (the|this) (role|opportunity|position|job|team|internship)/.test(n)) {
    const name = raw.replace(/^about\s+/i, '');
    return t('dashboard.jobs.sectionTitles.aboutCompanyNamed', {
      defaultValue: 'About {{company}}',
      company: name,
    });
  }
  if (n.startsWith('why join ')) {
    const name = raw.replace(/^why join\s+/i, '');
    return t('dashboard.jobs.sectionTitles.whyJoinNamed', {
      defaultValue: 'Why Join {{company}}',
      company: name,
    });
  }

  return raw;
}

/**
 * `true` when the row is one of the legacy rate-limit placeholders and
 * therefore not safe to render.  The next ingest run will re-parse it.
 */
export function isJunkJob(j: JunkCandidate): boolean {
  const company = (j.company ?? '').trim();
  if (!company || JUNK_COMPANIES.has(company)) return true;
  if (company.length <= 2) return true;

  const title = (j.title ?? '').trim();
  if (!title || JUNK_TITLES.has(title)) return true;

  const skills = Array.isArray(j.skills) ? j.skills : [];
  if (skills.length > 0 && skills.every(s => JUNK_SKILL_RE.test(s ?? ''))) return true;

  const desc = j.description ?? '';
  if (JUNK_REQ_RE.test(desc)) return true;

  return false;
}
